import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { axiosJson, base_api } from "../configs/fetcher";
import logo from "../assets/img/logo.png";

function EventRegistration() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [complete, setComplete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openMulti, setOpenMulti] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupMessage, setLookupMessage] = useState("");
  const eventApi =
    window.location.hostname === "localhost"
      ? "http://localhost/seameo-seps/service/api/v1"
      : base_api;

  useEffect(() => {
    axiosJson
      .get(`${eventApi}/events/${slug}`)
      .then((response) => setData(response.data))
      .catch((error) =>
        setMessage(
          error.response?.data?.message || "Unable to load this event."
        )
      )
      .finally(() => setLoading(false));
  }, [eventApi, slug]);

  const setValue = (key, value) => {
    setValues((currentValues) => {
      const nextValues = { ...currentValues, [key]: value };
      setErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        delete nextErrors[key];

        (data?.fields || [])
          .filter(
            (field) =>
              field.confirmation_field_key &&
              (field.field_key === key || field.confirmation_field_key === key)
          )
          .forEach((field) => {
            const confirmationValue = String(
              nextValues[field.field_key] || ""
            ).trim();
            const sourceValue = String(
              nextValues[field.confirmation_field_key] || ""
            ).trim();
            if (
              confirmationValue &&
              sourceValue &&
              confirmationValue !== sourceValue
            ) {
              const sourceField = (data?.fields || []).find(
                (item) => item.field_key === field.confirmation_field_key
              );
              nextErrors[field.field_key] =
                field.field_type === "email"
                  ? "Email addresses must match."
                  : `This value must match ${
                      sourceField?.label || field.confirmation_field_key
                    }.`;
            } else {
              delete nextErrors[field.field_key];
            }
          });

        if (Object.keys(nextErrors).length === 0) setMessage("");
        return nextErrors;
      });
      return nextValues;
    });
  };
  const fieldError = (field) => errors[field.field_key];
  const inputClass = (field) =>
    `event-input ${fieldError(field) ? "is-invalid" : ""}`;
  const otherKey = (field) => `${field.field_key}__other`;
  const shouldShowOther = (field) => {
    if (String(field.other_enabled) !== "1") return false;
    const selected = values[field.field_key];
    const trigger = field.other_option || "Other";
    return Array.isArray(selected)
      ? selected.includes(trigger)
      : selected === trigger;
  };

  const renderField = (field) => {
    const value = values[field.field_key] || "";
    const options = field.options || [];
    if (field.field_type === "textarea")
      return (
        <textarea
          className={inputClass(field)}
          rows="5"
          maxLength="500"
          placeholder={`Enter ${field.label.toLowerCase()} here ...`}
          value={value}
          onChange={(event) => setValue(field.field_key, event.target.value)}
        />
      );
    if (field.field_type === "select")
      return (
        <select
          className={inputClass(field)}
          value={value}
          onChange={(event) => setValue(field.field_key, event.target.value)}
        >
          <option value="">Select {field.label}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    if (field.field_type === "multiselect") {
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <div
          className={`event-multiselect ${
            fieldError(field) ? "is-invalid" : ""
          }`}
        >
          <button
            type="button"
            className={`event-input event-multi-button ${
              selectedValues.length ? "has-value" : ""
            }`}
            onClick={() =>
              setOpenMulti(openMulti === field.field_key ? "" : field.field_key)
            }
          >
            <span>
              {selectedValues.length
                ? selectedValues.join(", ")
                : `Select ${field.label}`}
            </span>
            <span className="event-multi-arrow">v</span>
          </button>
          {openMulti === field.field_key && (
            <div className="event-multi-menu">
              {options.map((option) => {
                const selected = selectedValues.includes(option);
                return (
                  <label key={option} className="event-multi-option">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(event) =>
                        setValue(
                          field.field_key,
                          event.target.checked
                            ? [...selectedValues, option]
                            : selectedValues.filter((item) => item !== option)
                        )
                      }
                    />
                    <span>{option}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (field.field_type === "radio")
      return (
        <div className="event-choice-list">
          {options.map((option) => (
            <label key={option} className="event-choice">
              <input
                type="radio"
                name={field.field_key}
                checked={value === option}
                onChange={() => setValue(field.field_key, option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    if (field.field_type === "checkbox")
      return (
        <div className="event-choice-list">
          {options.map((option) => {
            const selected = Array.isArray(value) && value.includes(option);
            return (
              <label key={option} className="event-choice">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(event) =>
                    setValue(
                      field.field_key,
                      event.target.checked
                        ? [...(Array.isArray(value) ? value : []), option]
                        : value.filter((item) => item !== option)
                    )
                  }
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      );
    return (
      <input
        type={field.field_type || "text"}
        className={inputClass(field)}
        placeholder={`Enter ${field.label.toLowerCase()}`}
        value={value}
        onChange={(event) => {
          setValue(field.field_key, event.target.value);
          if (field.field_key === "email") setLookupMessage("");
        }}
      />
    );
  };

  const scrollToFirstError = () => {
    window.setTimeout(() => {
      const firstError = document.querySelector(".event-error");
      const target =
        firstError?.closest(".event-field") ||
        document.querySelector(".event-alert");
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = target.querySelector("input, select, textarea, button");
      input?.focus({ preventScroll: true });
    }, 0);
  };

  const submit = (event) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    setMessage("");
    axiosJson
      .post(`${eventApi}/events/${slug}/register`, values)
      .then((response) => setComplete(response.data))
      .catch((error) => {
        setErrors(error.response?.data?.errors || {});
        setMessage(
          error.response?.data?.message || "Unable to complete registration."
        );
        scrollToFirstError();
      })
      .finally(() => setSaving(false));
  };

  const loadPreviousRegistration = () => {
    const email = String(values.email || "").trim();
    if (!email) {
      setLookupMessage("Please enter your email address.");
      return;
    }

    setLookupLoading(true);
    setLookupMessage("");
    axiosJson
      .post(`${eventApi}/events/${slug}/previous-registration`, { email })
      .then((response) => {
        setValues((currentValues) => ({
          ...currentValues,
          ...(response.data?.values || {}),
        }));
        setErrors({});
        setMessage("");
        setLookupMessage(
          "Your previous information has been added to the form."
        );
      })
      .catch((error) =>
        setLookupMessage(
          error.response?.data?.message ||
            "Unable to load your previous information."
        )
      )
      .finally(() => setLookupLoading(false));
  };

  return (
    <main className="event-page">
      <style>{`
    .event-page{text-align:left;min-height:100vh;background:#f3f3f3;color:#111;font-family:Arial,sans-serif;padding:32px 20px 60px}.event-shell{width:100%;max-width:760px;margin:0 auto;padding:60px 38px 50px;border:1px solid #eadfbd;border-radius:20px;background-color:#fbf4df;background-image:url("/bg-register.jpg");background-repeat:no-repeat;background-position:top center;background-size:cover;box-shadow:0 18px 50px rgba(72,91,44,.18)}.event-logo{display:block;width:90%;max-width:90%;margin:0 auto 34px}.event-email-lookup{position:relative}.event-email-lookup .event-input{padding-right:112px}.event-use-previous{position:absolute;top:5px;right:5px;height:33px;border:0;border-radius:7px;background:#1558a6;color:#fff;padding:0 11px;font-size:11px;font-weight:700;cursor:pointer}.event-use-previous:disabled{opacity:.65;cursor:wait}.event-previous-message{display:block;margin-top:7px;font-size:11px;color:#555}.event-step{font-size:12px;color:#b2b2b2;width:50%;margin:-30px auto 5px;text-align:start}.event-progress{width:50%;height:3px;background:#d0d0d0;position:relative;margin:0 auto}.event-progress>span{display:block;height:100%;width:50%;background:#ef164b}.event-progress.complete>span{width:100%}.event-progress.complete:after{content:"✓";position:absolute;right:-2px;top:50%;width:14px;height:14px;transform:translateY(-50%);border-radius:50%;background:#ef164b;color:#fff;font-size:10px;line-height:14px;text-align:center}.event-title{font-size:26px;line-height:1.1;margin:0 0 4px;font-weight:700}.event-subtitle{font-size:15px;margin:0 0 25px}.event-label{display:block;font-size:15px;font-weight:700;margin:0 0 3px}.event-label-hint{display:block;font-size:13px;font-weight:600;line-height:1.35;margin:6px 0 0;color:#333}.event-required{color:#ef164b}.event-field{margin-bottom:18px}.event-input{width:100%;height:43px;border:1px solid #a8a8a8;border-radius:10px;padding:0 17px;background:#fff;font-size:13px;outline:none}.event-input:focus{border-color:#555;box-shadow:0 0 0 2px rgba(0,0,0,.06)}textarea.event-input{height:auto;padding:13px 17px;resize:vertical}.event-input.is-invalid,.event-multiselect.is-invalid .event-input{border-color:#df263c}.event-hint,.event-error{display:block;font-size:12px;line-height:1.35;margin-top:5px}.event-hint{color:#555}.event-error{color:#df263c}.event-choice-list{display:grid;gap:8px;padding:4px 0}.event-choice{display:flex;align-items:center;gap:8px;font-size:13px}.event-choice input{accent-color:#ef164b}.event-other-field{margin-top:12px}.event-multiselect{position:relative}.event-multi-button{display:flex;align-items:center;justify-content:space-between;text-align:left;color:#999;cursor:pointer}.event-multi-button.has-value{color:#111}.event-multi-button span:first-child{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.event-multi-arrow{color:#111;margin-left:12px}.event-multi-menu{position:absolute;z-index:20;left:0;right:0;top:48px;max-height:230px;overflow:auto;border:1px solid #a8a8a8;border-radius:10px;background:#fff;box-shadow:0 12px 24px rgba(0,0,0,.12);padding:8px 0}.event-multi-option{display:flex;gap:9px;align-items:flex-start;padding:8px 14px;margin:0;font-size:13px;line-height:1.35}.event-multi-option input{margin-top:2px;accent-color:#ef164b}.event-alert{margin-bottom:18px;padding:11px 13px;border-radius:8px;font-size:13px;background:#ffe9ec;color:#a2112b}.event-button{width:100%;border:0;border-radius:10px;background:#ffb917;color:#000;font-size:15px;font-weight:700;padding:13px 18px;margin-top:18px;cursor:pointer}.event-button:disabled{opacity:.65;cursor:wait}.event-success{text-align:center;padding-top:18px}.event-success-image{display:block;width:250px;max-width:82%;margin:28px auto 30px}.event-success-mark{width:100px;height:100px;margin:8px auto 24px;border-radius:50%;display:grid;place-items:center;background:#fff;box-shadow:0 8px 25px rgba(0,0,0,.14);color:#7fbd5e;font-size:56px;font-weight:700}.event-envelope{width:220px;height:125px;background:linear-gradient(145deg,#fafafa,#dfdfdf);margin:0 auto 40px;border-radius:8px;box-shadow:0 10px 18px rgba(0,0,0,.15);position:relative}.event-envelope:after{content:"";position:absolute;left:15px;right:15px;top:16px;height:75px;background:#eee;clip-path:polygon(0 0,50% 72%,100% 0,100% 100%,0 100%)}.event-success h1{font-size:21px;margin:0 0 22px;display:flex;align-items:center;justify-content:center;gap:6px}.event-party-icon{width:22px;height:22px;object-fit:contain;flex:0 0 auto}.event-success p{font-size:14px;line-height:1.45;margin:0 0 18px}.event-number{font-size:13px;font-weight:700;margin-bottom:18px}@media(min-width:768px){.event-fields{display:flex;flex-wrap:wrap;margin:0 -8px}.event-field{padding:0 8px}.event-col-12{width:100%}.event-col-6{width:50%}.event-col-4{width:33.333%}.event-col-3{width:25%}}@media(max-width:767.98px){.event-page{padding:0px}.event-shell{max-width:100%;padding:50px 16px 32px;border-radius:0px;background-size:cover;background-position: top right;}.event-logo{display:block;width:80%;margin:0 auto 34px}.event-title{font-size:23px}.event-subtitle{font-size:14px;margin-bottom:20px}.event-fields{display:block}.event-field{width:100%;padding:0}.event-email-lookup .event-input{padding-right:104px}.event-use-previous{padding:0 9px;font-size:10px}.event-button{margin-top:10px}}@media(max-width:380px){.event-page{padding-left:8px;padding-right:8px}.event-shell{padding-left:12px;padding-right:12px}.event-email-lookup .event-input{padding-right:94px}.event-use-previous{padding:0 7px;font-size:9px}}
  `}</style>
      <div
        className="event-shell"
        style={
          data?.event?.registration_background
            ? {
                backgroundImage: `url("${data.event.registration_background}")`,
              }
            : undefined
        }
      >
        <img
          className="event-logo"
          src="/logo-register.png"
          alt="SEAMEO SEPS"
        />
        {complete ? (
          <section className="event-success">
            <div className="event-step">Step 2/2</div>
            <div className="event-progress complete">
              <span />
            </div>
            <img
              className="event-success-image"
              src="/success.png"
              alt="Registration successful"
            />
            <h1 className="event-number">
              Registration Successful{" "}
              <img className="event-party-icon" src="/party.svg" alt="" />
            </h1>
            <p className="px-5">
              Thank you for registering for the SEAMEO SEPS Learning Series.
              <br />
              You will receive a confirmation email within 2 business days.
              <br />
              <br />
              If you do not receive the email within this timeframe, please
              contact us at
              <br />
              <a href="mailto:secretariat@seameoseps.org">
                secretariat@seameoseps.org
              </a>
            </p>
            {/* <p className="event-number">
              Registration No. {complete.registration_no}
            </p> */}
            <button
              className="event-button"
              onClick={() => window.location.assign("/")}
            >
              Done
            </button>
          </section>
        ) : (
          <section className="event-success text-start">
            <div className="event-step">Step 1/2</div>
            <div className="event-progress">
              <span />
            </div>
            <h1 className="event-title pt-5">Registration form</h1>
            <p className="event-subtitle">
              Registration form information required
            </p>
            {message && <div className="event-alert">{message}</div>}
            {loading ? (
              <p>Loading form...</p>
            ) : data?.event?.registration_opened ? (
              <form onSubmit={submit}>
                <div className="event-fields">
                  {data.fields.map((field, index) => (
                    <div
                      className={`event-field event-col-${
                        field.column_width || 12
                      }`}
                      key={field.field_key}
                    >
                      <label className="event-label">
                        {index + 1}. {field.label}{" "}
                        {String(field.is_required) === "1" && (
                          <span className="event-required">*</span>
                        )}
                      </label>

                      {field.field_key === "email" ? (
                        <>
                          <div className="event-email-lookup">
                            {renderField(field)}
                            <button
                              type="button"
                              className="event-use-previous"
                              disabled={lookupLoading}
                              onClick={loadPreviousRegistration}
                            >
                              {lookupLoading ? "Loading..." : "Use previous"}
                            </button>
                          </div>
                          {lookupMessage && (
                            <small className="event-previous-message">
                              {lookupMessage}
                            </small>
                          )}
                        </>
                      ) : (
                        renderField(field)
                      )}
                      {shouldShowOther(field) && (
                        <div className="event-other-field">
                          <label
                            className="event-label"
                            htmlFor={otherKey(field)}
                          >
                            {field.other_label || "Please specify"}{" "}
                            <span className="event-required">*</span>
                          </label>
                          <input
                            id={otherKey(field)}
                            type="text"
                            className={`event-input ${
                              errors[otherKey(field)] ? "is-invalid" : ""
                            }`}
                            value={values[otherKey(field)] || ""}
                            onChange={(event) =>
                              setValue(otherKey(field), event.target.value)
                            }
                          />
                          {errors[otherKey(field)] && (
                            <small className="event-error">
                              {errors[otherKey(field)]}
                            </small>
                          )}
                        </div>
                      )}
                      {field.field_type === "multiselect" && (
                        <small className="event-label-hint">
                          (multiple selection allowed)
                        </small>
                      )}
                      {field.help_text && (
                        <small className="event-hint">
                          {field.help_text ===
                          "Email addresses must match. Otherwise, an error message will appear."
                            ? "Must match the email address."
                            : field.help_text}
                        </small>
                      )}
                      {fieldError(field) && (
                        <small className="event-error">
                          {fieldError(field)}
                        </small>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="submit"
                  className="event-button"
                  disabled={saving}
                >
                  {saving ? "Submitting..." : "Confirm"}
                </button>
              </form>
            ) : (
              <div className="event-alert">
                Registration is not open at this time.
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

export default EventRegistration;
